import React, { useEffect, useState, useRef } from "react";
import "../../assets/css/Services.css";
import { useNavigate } from "react-router-dom";
import AOS from "aos";
import "aos/dist/aos.css";

// meta data
import { Helmet } from "react-helmet";
import data from "../../component/ApiUrl/meta_api";

/* ─── Data ─────────────────────────────────────────────────────────────────── */

const cards = [
  {
    icon: "fa-laptop-code",
    title: "Web & Mobile App Development",
    para: "We build high-performance, scalable web and mobile applications tailored to your business goals from sleek landing pages to complex enterprise-grade platforms.",
    list: [
      "Full Stack Web Development",
      "Android & iOS App Development",
      "Cross-Platform App Development",
      "Progressive Web Apps (PWA)",
    ],
  },

  {
    icon: "fa-brain",
    title: "AI, Data Science & Machine Learning",
    para: "Unlock the power of artificial intelligence and data science to automate processes, uncover insights, and build intelligent systems that drive smarter business decisions.",
    list: [
      "AI & ML Model Development",
      "Data Science & Predictive Analytics",
      "NLP & Chatbot Development",
      "Business Intelligence & Reporting",
    ],
  },

  {
    icon: "fa-cloud",
    title: "Cloud Computing & DevOps",
    para: "Accelerate your digital transformation with our cloud infrastructure and DevOps services enabling faster deployments, greater scalability, and reduced operational overhead.",
    list: [
      "Cloud Setup & Migration (AWS/Azure)",
      "CI/CD Pipeline & Automation",
      "Docker & Kubernetes Management",
      "Server Setup & Monitoring",
    ],
  },

  {
    icon: "fa-shield-halved",
    title: "Cybersecurity Solutions",
    para: "Protect your digital assets, data, and infrastructure with our comprehensive cybersecurity services identifying vulnerabilities and implementing robust defence strategies.",
    list: [
      "Penetration Testing & Security Audits",
      "Network Security Solutions",
      "Data Backup & Disaster Recovery",
      "Compliance & Risk Management",
    ],
  },

  {
    icon: "fa-diagram-project",
    title: "Custom Software & ERP Solutions",
    para: "We deliver tailor-made software solutions from enterprise resource planning systems to custom business tools designed to streamline operations and enhance productivity.",
    list: [
      "Custom Software Development",
      "ERP & CRM Development",
      "API Integration & Development",
      "Legacy System Modernization",
    ],
  },

  {
    icon: "fa-bug",
    title: "Software Testing, QA & IT Support",
    para: "Ensure flawless software performance with our rigorous quality assurance and testing services backed by reliable IT support to keep your systems running without interruption.",
    list: [
      "Manual & Automated Testing",
      "Performance & Load Testing",
      "IT Consulting & Advisory",
      "Technical Support & Maintenance",
    ],
  },
];

const STEPS = [
  {
    icon: "fa-magnifying-glass",
    title: "Requirement Analysis",
    desc: "Analyze business requirements, existing systems, and identify suitable software solutions.",
  },
  {
    icon: "fa-diagram-project",
    title: "Solution Architecture & Planning",
    desc: "Design scalable architecture, select technologies, and plan project development roadmap.",
  },
  {
    icon: "fa-laptop-code",
    title: "Development & System Integration",
    desc: "Develop secure applications and integrate systems, databases, and cloud platforms.",
  },
  {
    icon: "fa-vial-circle-check",
    title: "Testing & Quality Assurance",
    desc: "Perform functional testing, fix bugs, and ensure stable software performance.",
  },
  {
    icon: "fa-rocket",
    title: "Deployment & Continuous Support",
    desc: "Deploy software, monitor performance, and provide ongoing updates and support.",
  },
];

const FAQ_DATA = [
  [
    "What IT & Software services do you offer?",
    "We provide comprehensive IT and Software services including custom software development, web and mobile application development, cloud solutions, AI integration, system integration, and business automation to help organizations improve efficiency and scale their digital operations.",
  ],
  [
    "How can IT solutions help my business grow?",
    "Modern IT solutions help businesses automate processes, improve productivity, enhance data security, and create scalable digital systems that support long-term growth and operational efficiency.",
  ],
  [
    "What technologies do you use for software development?",
    "Our team uses modern technologies such as Java, Spring Boot, React, Node.js, cloud platforms, and advanced frameworks to build secure, scalable, and high-performance software solutions.",
  ],
  [
    "Do you offer cloud solutions and migration services?",
    "Yes, we help businesses migrate their applications and infrastructure to cloud platforms, ensuring scalability, improved performance, secure data management, and cost efficiency.",
  ],
  [
    "Can you integrate AI into business applications?",
    "Yes, we develop AI-powered solutions including automation systems, predictive analytics, and intelligent applications that improve decision-making and operational efficiency.",
  ],
  [
    "How long does it take to develop a software solution?",
    "The development timeline depends on project complexity, features, and integration requirements. Small projects may take weeks, while enterprise solutions may take several months.",
  ],
  [
    "How can I get started with your IT & Software services?",
    "You can contact Skimitar Hindustan Pvt Ltd for a consultation. Our experts will analyze your business needs and provide a customized IT solution strategy and development plan.",
  ],
];

/* ─── Small components ──────────────────────────────────────────────────────── */
function SectionLabel({ text }) {
  return (
    <p
      className="fw-semibold text-uppercase mb-1"
      style={{ fontSize: 20, color: "#cc0000", letterSpacing: 1 }}
    >
      {text}
    </p>
  );
}

function FAQItem({ q, a, idx }) {
  const [open, setOpen] = useState(idx === 0);
  return (
    <div
      className={`rs-faq-item rs-anim${open ? " open" : ""}`}
      style={{ transitionDelay: `${idx * 55}ms` }}
    >
      <button className="rs-faq-btn" onClick={() => setOpen((o) => !o)}>
        <span className="rs-faq-q">{q}</span>
        <span className="rs-faq-icon">
          <i className="fa-solid fa-plus" />
        </span>
      </button>
      <div className="rs-faq-body">{a}</div>
    </div>
  );
}

/* ─── Main Page ─────────────────────────────────────────────────────────────── */
export default function ItandSoftware() {
  const navigate = useNavigate();
  const [meta, setMeta] = useState(null);

  // scroll top
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, []);

  const fetchData = () => {
    const ItandSoftwareMeta = data[0]?.information_technology_page;
    if (ItandSoftwareMeta) {
      setMeta(ItandSoftwareMeta);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    AOS.init({
      duration: 800,
      once: true,
      easing: "ease-in-out",
    });
  }, []);

  return (
    <div className="body">
      <Helmet>
        <title>{meta ? meta.title : "IT & Software"}</title>
      </Helmet>

      {/* ═════════
     1. HERO 
   ═════════════ */}
      <div
        className="backgroundCommenImage position-relative d-flex align-items-center mb-5"
        style={{
          backgroundImage: `url(${require("../../assets/img/Service/It and Software/banner.jpg")})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          minHeight: "85vh",
        }}
      >
     
      </div>

      {/* ════════════
     2. INTRO 
   ═════════════════ */}
      <div className="container margin_top container_width">
        <div className="row align-items-center justify-content-center g-5">
          {/* Left */}
          <div className="col-lg-6" data-aos="fade-right" data-aos-delay="100">
            <SectionLabel text="IT & Software Services" />

            <h2 className="fw-semibold mb-3" style={{ fontSize: 20 }}>
              About Our IT & Software Solutions
            </h2>

            <p className="mb-3 content-text">
              <span className="fw-semibold">Skimitar Hindustan Pvt Ltd</span>{" "}
              delivers innovative IT and Software solutions including custom
              software development, cloud platforms, AI integration, and
              business automation to help businesses improve efficiency,
              streamline operations, and accelerate digital transformation.
            </p>

            <ul className="list-unstyled content-list mt-3">
              {[
                "Develop custom software solutions tailored to your business needs",
                "Build secure and scalable cloud-based applications",
                "Integrate AI-powered automation to improve operational efficiency",
                "Streamline business processes through enterprise software systems",
                "Improve productivity with modern IT infrastructure and digital tools",
                "Support business growth with future-ready technology solutions",
              ].map((item, i) => (
                <li
                  key={i}
                  className="d-flex align-items-start gap-2 mb-2 small"
                  data-aos="fade-up"
                  data-aos-delay={`${100 + i * 80}`}
                >
                  <i className="fa-solid fa-check text-danger mt-1"></i>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Right */}
          <div
            className="col-lg-6 text-center"
            data-aos="fade-right"
            data-aos-delay="200"
          >
            <div className="position-relative d-inline-block w-100">
              {/* Image */}
              <img
                className="img-fluid rounded-4 "
                src={require("../../assets/img/Service/It and Software/img-1.jpg")}
                alt="DM Image-1"
              />

              {/* Stat */}
              <div className="retro-stat">
                <i class="fa-solid fa-laptop-code text-red fs-5"></i>
                <div>
                  <div className="fw-bold fs-5">170+</div>
                  <div className="small text-muted">
                    IT & Software Solutions
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════
     3. SERVICE DETAILS tabs + feature list
   ══════════════════════════════════════════════════════════════ */}
      <section className="margin_top pb-5">
        <div className="container container_width">
          <div className="mb-1" data-aos="fade-up">
            <SectionLabel text="IT & Software Services" />
          </div>

          <div className="row mb-4">
            <div className="col-lg-7" data-aos="fade-up" data-aos-delay="100">
              <h2 className="fw-semibold mb-2" style={{ fontSize: 20 }}>
                Smart Technology Solutions for Modern Businesses
              </h2>

              <p className="mb-3 content-text">
                With{" "}
                <span className="fw-semibold">Skimitar Hindustan Pvt Ltd</span>,
                provides advanced IT and software services including custom
                development, cloud technologies, AI automation, and enterprise
                applications to help businesses build scalable and future-ready
                digital systems.
              </p>
            </div>

            <div
              className="col-lg-5 text-lg-end mt-3 mt-lg-0"
              data-aos="fade-right"
              data-aos-delay="100"
            >
              <a href="#contact" className="btn btn-red px-4 py-2">
                Discuss Your Project
                <i className="fa-solid fa-arrow-right ms-2"></i>
              </a>
            </div>
          </div>

          {/* Content */}
          <div className="row g-3">
            {cards.map((s, i) => (
              <div
                key={i}
                className="col-12 col-sm-6 col-lg-4"
                data-aos="fade-up"
                data-aos-delay={`${i * 100}`}
              >
                <div className="card h-100 border-0 shadow service-card">
                  <div className="card-body position-relative p-4">
                    {/* Number */}
                    {/* <span className="service-number">
                      {String(i + 1).padStart(2, "0")}
                    </span> */}

                    {/* Icon */}
                    <div className="service-icon mb-3">
                      <i className={`fa-solid ${s.icon}`}></i>
                    </div>

                    {/* title */}
                    <h5
                      className="fw-bold mb-2 text-red"
                      style={{ fontSize: 15 }}
                    >
                      {s.title}
                    </h5>

                    {/* para */}
                    <p className="small text-muted mb-2">{s.para}</p>

                    {/* list */}
                    <ul className="list-unstyled">
                      {s.list.map((item, i) => (
                        <li
                          key={i}
                          className="d-flex align-items-start gap-2 mb-2 small"
                          data-aos="fade-up"
                          data-aos-delay={`${100 + i * 80}`}
                        >
                          <i className="fa-solid fa-check text-danger mt-1"></i>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>

                    <button className="btn btn-red btn-sm mt-2">
                      Enquire Now
                      <i className="fa-solid fa-arrow-right ms-2"></i>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════
     4. HOW IT WORKS 5-step process
   ══════════════════════════════════════════════════════════════ */}
      <section className="margin_top py-5 bg-light" id="process">
        <div className="container container_width">
          <div className="mb-2" data-aos="fade-up">
            <SectionLabel text="How Our IT & Software Solutions Work" />
          </div>

          <div className="mb-5" data-aos="fade-up" data-aos-delay="100">
            <h2 className="fw-semibold mb-3" style={{ fontSize: 20 }}>
              Our 5-Step IT & Software Development Process
            </h2>
            <p className="mb-3 content-text">
              At <span className="fw-semibold">Skimitar Hindustan Pvt Ltd</span>
              , our IT and software development approach focuses on strategic
              planning, modern technologies, and scalable solutions aligned with
              business goals.
            </p>
          </div>

          <div className="row g-4 text-center justify-content-center">
            {STEPS.map((step, i) => (
              <div
                key={i}
                className="col-12 col-md-4 col-lg position-relative"
                data-aos="fade-up"
                data-aos-delay={`${i * 120}`}
              >
                {/* connector */}
                {i < STEPS.length - 1 && (
                  <div className="process-line d-none d-lg-block"></div>
                )}

                {/* circle */}
                <div className="process-circle mx-auto mb-3 d-flex align-items-center justify-content-center position-relative">
                  <i className={`fa-solid ${step.icon} text-red fs-5`}></i>

                  <span className="badge bg-red position-absolute process-number">
                    {i + 1}
                  </span>
                </div>

                <h5 className="fw-bold small mb-1">{step.title}</h5>

                <p className="text-muted small mb-0">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════
     5. FAQ
   ══════════════════════════════════════════════════════════════ */}
      <section className="margin_top rs-bg-alt" id="faq">
        <div className="container container_width py-5">
          <div className="text-center rs-anim mb-1" data-aos="fade-up">
            <SectionLabel text="FAQ" />
          </div>
          <div
            className="text-center rs-anim mb-5"
            data-aos="fade-up"
            data-aos-delay="100"
          >
            <h2 className="fw-semibold" style={{ fontSize: 20 }}>
              Frequently Asked Questions
            </h2>
            <p className="mb-3 content-text text-center">
              Learn everything about our IT & Software Services, including
              custom development, cloud solutions, AI integration, and scalable
              business automation.
            </p>
          </div>

          <div className="row justify-content-center">
            <div className="col-lg-9">
              {FAQ_DATA.map(([q, a], i) => (
                <FAQItem key={i} q={q} a={a} idx={i} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════
     6. GET IN TOUCH CTA
   ══════════════════════════════════════════════════════════════ */}
      <section id="contact">
        <div className="p-5" style={{ background: "#cc0000" }}>
          <div
            className="d-flex align-items-center justify-content-center gap-3 mb-3"
            data-aos="fade-down"
          >
            <div
              className="bg-light"
              style={{ width: "40px", height: "1px" }}
            />
            <span
              className="text-uppercase fw-semibold text-white text-opacity-75"
              style={{
                letterSpacing: "2px",
                whiteSpace: "nowrap",
                fontSize: "13px",
              }}
            >
              Transform Your Business with Technology
            </span>
            <div
              className="bg-light"
              style={{ width: "40px", height: "1px" }}
            />
          </div>

          <div className="row g-4 align-items-center">
            <div
              className="col-lg-7 text-center text-lg-start"
              data-aos="fade-right"
              data-aos-delay="100"
            >
              <h2 className="fw-bolder text-white text-uppercase mb-3">
                Empower Your Business with IT &<br />
                Software Solutions
              </h2>

              <p
                className="text-white text-opacity-75 mb-0"
                style={{ lineHeight: "1.75" }}
              >
                Accelerate your business growth with our expert IT & Software
                Services. We build scalable software, modern web applications,
                and cloud-based solutions that improve efficiency and
                productivity. Our team specializes in custom software
                development, system integration, and business automation to help
                organizations streamline operations and achieve reliable digital
                transformation.
              </p>
            </div>

            <div
              className="col-lg-5"
              data-aos="fade-right"
              data-aos-delay="200"
            >
              <div className="d-flex flex-column flex-sm-row gap-3 justify-content-center justify-content-lg-end">
                <button
                  className="btn oc-btn-white px-4 py-3 fw-bold text-uppercase"
                  onClick={() => navigate("/contact")}
                >
                  Get Free Consultation →
                </button>

                <a
                  className="btn oc-btn-ghost px-4 py-3 fw-bold text-uppercase"
                  href="tel:8428010909"
                >
                  📞 (+91) 8428010909
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
